/**
 * Breakout Game
 * A classic arcade game where the player breaks bricks with a bouncing ball using a paddle.
 */

import { getHighScores, type HighScoreEntry } from '../state/highscore'

// ============================================================================
// Types
// ============================================================================

/** Callback function called when game ends */
export type GameOverCallback = (score: number) => void

interface Ball {
  x: number
  y: number
  dx: number
  dy: number
  radius: number
}

interface Paddle {
  x: number
  y: number
  width: number
  height: number
  speed: number
}

interface Brick {
  x: number
  y: number
  width: number
  height: number
  alive: boolean
}

interface GameState {
  ball: Ball
  paddle: Paddle
  bricks: Brick[]
  score: number
  lives: number
  gameOver: boolean
  won: boolean
  started: boolean
  ballLaunched: boolean
  canvasWidth: number
  canvasHeight: number
}

// ============================================================================
// Constants
// ============================================================================

const BALL_RADIUS = 8
const BALL_SPEED = 5
const PADDLE_WIDTH = 100
const PADDLE_HEIGHT = 12
const PADDLE_SPEED = 8
const BRICK_WIDTH = 60
const BRICK_HEIGHT = 20
const BRICK_ROWS = 5
const BRICK_COLS = 8
const BRICK_PADDING = 5
const INITIAL_LIVES = 3
const SCORE_PER_BRICK = 10
const WIN_SCORE_MULTIPLIER = 2

/** Game version */
export const BREAKOUT_VERSION = '1.0.0'

// ============================================================================
// Game Logic (Exported for Testing)
// ============================================================================

/**
 * Create the paddle at the bottom center of the screen
 */
export function createPaddle(canvasWidth: number, canvasHeight: number): Paddle {
  return {
    x: canvasWidth / 2 - PADDLE_WIDTH / 2,
    y: canvasHeight - 40,
    width: PADDLE_WIDTH,
    height: PADDLE_HEIGHT,
    speed: PADDLE_SPEED,
  }
}

/**
 * Create the ball positioned on the paddle
 */
export function createBall(paddle: Paddle): Ball {
  return {
    x: paddle.x + paddle.width / 2,
    y: paddle.y - BALL_RADIUS,
    dx: 0,
    dy: 0,
    radius: BALL_RADIUS,
  }
}

/**
 * Create the brick grid
 */
export function createBricks(canvasWidth: number): Brick[] {
  const bricks: Brick[] = []
  const startX = (canvasWidth - (BRICK_COLS * BRICK_WIDTH + (BRICK_COLS - 1) * BRICK_PADDING)) / 2
  const startY = 50

  for (let row = 0; row < BRICK_ROWS; row++) {
    for (let col = 0; col < BRICK_COLS; col++) {
      bricks.push({
        x: startX + col * (BRICK_WIDTH + BRICK_PADDING),
        y: startY + row * (BRICK_HEIGHT + BRICK_PADDING),
        width: BRICK_WIDTH,
        height: BRICK_HEIGHT,
        alive: true,
      })
    }
  }

  return bricks
}

/**
 * Check collision between ball and rectangle (paddle or brick)
 */
export function checkBallRectCollision(
  ball: Ball,
  rect: { x: number; y: number; width: number; height: number }
): boolean {
  const ballLeft = ball.x - ball.radius
  const ballRight = ball.x + ball.radius
  const ballTop = ball.y - ball.radius
  const ballBottom = ball.y + ball.radius

  const rectLeft = rect.x
  const rectRight = rect.x + rect.width
  const rectTop = rect.y
  const rectBottom = rect.y + rect.height

  return (
    ballRight >= rectLeft && ballLeft <= rectRight && ballBottom >= rectTop && ballTop <= rectBottom
  )
}

/**
 * Handle ball collision with paddle
 * Returns new ball velocity based on collision point
 */
export function handlePaddleCollision(ball: Ball, paddle: Paddle): { dx: number; dy: number } {
  // Calculate where on the paddle the ball hit (0 = left, 1 = right)
  const hitPos = (ball.x - paddle.x) / paddle.width

  // Angle ranges from -60 to 60 degrees
  const angle = ((hitPos - 0.5) * Math.PI) / 3

  // Ensure ball always goes upward
  const speed = Math.sqrt(ball.dx * ball.dx + ball.dy * ball.dy)
  const newDx = Math.sin(angle) * speed
  const newDy = -Math.abs(Math.cos(angle) * speed)

  return { dx: newDx, dy: newDy }
}

/**
 * Move the paddle left or right, keeping it within bounds
 */
export function movePaddle(
  paddle: Paddle,
  direction: 'left' | 'right',
  canvasWidth: number
): Paddle {
  const newX = direction === 'left' ? paddle.x - paddle.speed : paddle.x + paddle.speed

  return {
    ...paddle,
    x: Math.max(0, Math.min(newX, canvasWidth - paddle.width)),
  }
}

/**
 * Update ball position and handle wall collisions
 */
export function updateBall(ball: Ball, canvasWidth: number): Ball {
  let { x, y, dx, dy } = ball
  const { radius } = ball

  x += dx
  y += dy

  // Wall collisions
  if (x - radius <= 0 || x + radius >= canvasWidth) {
    dx = -dx
    x = Math.max(radius, Math.min(x, canvasWidth - radius))
  }

  if (y - radius <= 0) {
    dy = -dy
    y = radius
  }

  return { x, y, dx, dy, radius }
}

/**
 * Launch the ball from the paddle with random horizontal direction
 */
export function launchBall(ball: Ball, paddle: Paddle): Ball {
  const angle = ((Math.random() - 0.5) * Math.PI) / 2 // Random angle between -45 and 45 degrees
  const speed = BALL_SPEED

  return {
    ...ball,
    x: paddle.x + paddle.width / 2,
    y: paddle.y - ball.radius,
    dx: Math.sin(angle) * speed,
    dy: -Math.abs(Math.cos(angle) * speed),
  }
}

/**
 * Check if ball has fallen below the paddle (lose life)
 */
export function isBallLost(ball: Ball, canvasHeight: number): boolean {
  return ball.y - ball.radius > canvasHeight
}

/**
 * Check if all bricks are destroyed
 */
export function isGameWon(bricks: Brick[]): boolean {
  return bricks.every((brick) => !brick.alive)
}

/**
 * Handle brick collision - returns the brick index if hit, -1 otherwise
 */
export function checkBrickCollision(ball: Ball, bricks: Brick[]): number {
  for (let i = 0; i < bricks.length; i++) {
    const brick = bricks[i]
    if (brick.alive && checkBallRectCollision(ball, brick)) {
      return i
    }
  }
  return -1
}

/**
 * Handle ball collision with a brick - reverse appropriate velocity
 */
export function handleBrickCollision(ball: Ball, brick: Brick): { dx: number; dy: number } {
  const ballLeft = ball.x - ball.radius
  const ballRight = ball.x + ball.radius
  const ballTop = ball.y - ball.radius
  const ballBottom = ball.y + ball.radius

  const brickLeft = brick.x
  const brickRight = brick.x + brick.width
  const brickTop = brick.y
  const brickBottom = brick.y + brick.height

  let newDx = ball.dx
  let newDy = ball.dy

  // Determine collision side based on overlap
  const overlapLeft = ballRight - brickLeft
  const overlapRight = brickRight - ballLeft
  const overlapTop = ballBottom - brickTop
  const overlapBottom = brickBottom - ballTop

  const minOverlap = Math.min(overlapLeft, overlapRight, overlapTop, overlapBottom)

  if (minOverlap === overlapLeft || minOverlap === overlapRight) {
    newDx = -newDx // Horizontal collision
  } else {
    newDy = -newDy // Vertical collision
  }

  return { dx: newDx, dy: newDy }
}

/**
 * Create the initial game state
 */
export function createGameState(canvasWidth: number, canvasHeight: number): GameState {
  const paddle = createPaddle(canvasWidth, canvasHeight)
  const ball = createBall(paddle)
  const bricks = createBricks(canvasWidth)

  return {
    ball,
    paddle,
    bricks,
    score: 0,
    lives: INITIAL_LIVES,
    gameOver: false,
    won: false,
    started: false,
    ballLaunched: false,
    canvasWidth,
    canvasHeight,
  }
}

/**
 * Update the game state for one frame
 */
export function updateGame(
  state: GameState,
  input: { left: boolean; right: boolean; launch: boolean }
): GameState {
  if (state.gameOver || state.won) {
    return state
  }

  // Handle input
  const newState = { ...state }
  if (input.left) {
    newState.paddle = movePaddle(newState.paddle, 'left', newState.canvasWidth)
  }
  if (input.right) {
    newState.paddle = movePaddle(newState.paddle, 'right', newState.canvasWidth)
  }

  // If not started, only allow paddle movement
  if (!newState.started) {
    return newState
  }

  // Launch ball if requested and not already launched
  if (input.launch && !newState.ballLaunched) {
    newState.ball = launchBall(newState.ball, newState.paddle)
    newState.ballLaunched = true
  }

  // Update ball position if launched
  if (newState.ballLaunched) {
    newState.ball = updateBall(newState.ball, newState.canvasWidth)

    // Check paddle collision
    if (checkBallRectCollision(newState.ball, newState.paddle)) {
      const newVelocity = handlePaddleCollision(newState.ball, newState.paddle)
      newState.ball.dx = newVelocity.dx
      newState.ball.dy = newVelocity.dy
      // Move ball above paddle to prevent multiple collisions
      newState.ball.y = newState.paddle.y - newState.ball.radius
    }

    // Check brick collisions
    const brickIndex = checkBrickCollision(newState.ball, newState.bricks)
    if (brickIndex !== -1) {
      const brick = newState.bricks[brickIndex]
      const newVelocity = handleBrickCollision(newState.ball, brick)
      newState.ball.dx = newVelocity.dx
      newState.ball.dy = newVelocity.dy

      // Destroy brick and update score
      newState.bricks = newState.bricks.map((b, i) =>
        i === brickIndex ? { ...b, alive: false } : b
      )
      newState.score += SCORE_PER_BRICK
    }

    // Check if ball is lost
    if (isBallLost(newState.ball, newState.canvasHeight)) {
      newState.lives -= 1
      newState.ballLaunched = false
      newState.ball = createBall(newState.paddle)

      if (newState.lives <= 0) {
        newState.gameOver = true
      }
    }

    // Check win condition
    if (isGameWon(newState.bricks)) {
      newState.won = true
      newState.score *= WIN_SCORE_MULTIPLIER
    }
  } else {
    // Ball follows paddle when not launched
    newState.ball.x = newState.paddle.x + newState.paddle.width / 2
  }

  return newState
}

// ============================================================================
// Rendering
// ============================================================================

function drawPaddle(ctx: CanvasRenderingContext2D, paddle: Paddle): void {
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(paddle.x, paddle.y, paddle.width, paddle.height)
}

function drawBall(ctx: CanvasRenderingContext2D, ball: Ball): void {
  ctx.fillStyle = '#ffffff'
  ctx.beginPath()
  ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2)
  ctx.fill()
}

function drawBrick(ctx: CanvasRenderingContext2D, brick: Brick): void {
  if (!brick.alive) return

  // Different colors for different rows
  const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57']
  const row = Math.floor(brick.y / (BRICK_HEIGHT + BRICK_PADDING)) % colors.length

  ctx.fillStyle = colors[row]
  ctx.fillRect(brick.x, brick.y, brick.width, brick.height)

  // Border
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 1
  ctx.strokeRect(brick.x, brick.y, brick.width, brick.height)
}

function drawBricks(ctx: CanvasRenderingContext2D, bricks: Brick[]): void {
  bricks.forEach((brick) => drawBrick(ctx, brick))
}

function drawUI(
  ctx: CanvasRenderingContext2D,
  score: number,
  lives: number,
  canvasWidth: number
): void {
  ctx.fillStyle = '#ffffff'
  ctx.font = '16px "Press Start 2P", monospace'
  ctx.textAlign = 'left'
  ctx.fillText(`SCORE: ${score}`, 20, 30)

  ctx.fillText(`LIVES: ${lives}`, 20, 60)

  // Draw remaining bricks count
  const remainingBricks = Math.max(0, BRICK_ROWS * BRICK_COLS - score / SCORE_PER_BRICK)
  ctx.fillText(`BRICKS: ${remainingBricks}`, canvasWidth - 200, 30)
}

function drawStartScreen(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  highScores: HighScoreEntry[]
): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  ctx.fillStyle = '#ffffff'
  ctx.font = '48px "Press Start 2P", monospace'
  ctx.textAlign = 'center'
  ctx.fillText('BREAKOUT', canvasWidth / 2, canvasHeight / 2 - 120)

  ctx.font = '14px "Press Start 2P", monospace'
  ctx.fillText('Break all the bricks!', canvasWidth / 2, canvasHeight / 2 - 70)

  // Show high scores if available
  if (highScores.length > 0) {
    ctx.fillStyle = '#f97316' // orange
    ctx.font = '10px "Press Start 2P", monospace'
    ctx.fillText('HIGH SCORES', canvasWidth / 2, canvasHeight / 2 - 35)

    ctx.fillStyle = '#9ca3af' // gray
    ctx.font = '9px "Press Start 2P", monospace'
    const topScores = highScores.slice(0, 3)
    topScores.forEach((entry, index) => {
      const y = canvasHeight / 2 - 10 + index * 18
      ctx.fillText(
        `${index + 1}. ${entry.name}  ${entry.score.toLocaleString()}`,
        canvasWidth / 2,
        y
      )
    })
  }

  const controlsY = highScores.length > 0 ? canvasHeight / 2 + 55 : canvasHeight / 2 + 20

  ctx.fillStyle = '#4ecdc4'
  ctx.font = '12px "Press Start 2P", monospace'
  ctx.fillText('CONTROLS', canvasWidth / 2, controlsY)

  ctx.fillStyle = '#9ca3af' // Gray
  ctx.font = '10px "Press Start 2P", monospace'
  ctx.fillText('Arrow Keys / A-D - Move Paddle', canvasWidth / 2, controlsY + 25)
  ctx.fillText('Space - Start & Launch Ball', canvasWidth / 2, controlsY + 45)

  ctx.fillStyle = '#4ecdc4'
  ctx.font = '14px "Press Start 2P", monospace'
  ctx.fillText('Press SPACE to start', canvasWidth / 2, controlsY + 90)
}

function drawGameOver(
  ctx: CanvasRenderingContext2D,
  score: number,
  canvasWidth: number,
  canvasHeight: number
): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  ctx.fillStyle = '#ff6b6b'
  ctx.font = '32px "Press Start 2P", monospace'
  ctx.textAlign = 'center'
  ctx.fillText('GAME OVER', canvasWidth / 2, canvasHeight / 2 - 40)

  ctx.fillStyle = '#ffffff'
  ctx.font = '16px "Press Start 2P", monospace'
  ctx.fillText(`FINAL SCORE: ${score}`, canvasWidth / 2, canvasHeight / 2 + 20)

  ctx.fillStyle = '#4ecdc4'
  ctx.font = '12px "Press Start 2P", monospace'
  ctx.fillText('Press ENTER to restart', canvasWidth / 2, canvasHeight / 2 + 60)
}

function drawWinScreen(
  ctx: CanvasRenderingContext2D,
  score: number,
  canvasWidth: number,
  canvasHeight: number
): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  ctx.fillStyle = '#feca57'
  ctx.font = '32px "Press Start 2P", monospace'
  ctx.textAlign = 'center'
  ctx.fillText('YOU WIN!', canvasWidth / 2, canvasHeight / 2 - 40)

  ctx.fillStyle = '#ffffff'
  ctx.font = '16px "Press Start 2P", monospace'
  ctx.fillText(`FINAL SCORE: ${score}`, canvasWidth / 2, canvasHeight / 2 + 20)

  ctx.fillStyle = '#4ecdc4'
  ctx.font = '12px "Press Start 2P", monospace'
  ctx.fillText('Press ENTER to restart', canvasWidth / 2, canvasHeight / 2 + 60)
}

function drawLaunchPrompt(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number
): void {
  ctx.fillStyle = '#ffffff'
  ctx.font = '14px "Press Start 2P", monospace'
  ctx.textAlign = 'center'
  ctx.fillText('Press SPACE to launch ball', canvasWidth / 2, canvasHeight - 50)
}

// ============================================================================
// Main Game Loop
// ============================================================================

/**
 * Initialize and run the Breakout game
 * @param container The HTML element to render the game in
 * @param onGameOver Optional callback called when game ends with the final score
 * @returns Cleanup function to stop the game
 */
export function loadBreakout(container: HTMLElement, onGameOver?: GameOverCallback): () => void {
  // Create canvas
  const canvas = document.createElement('canvas')
  canvas.style.width = '100%'
  canvas.style.height = '100%'
  canvas.style.display = 'block'
  container.innerHTML = ''
  container.appendChild(canvas)

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    console.error('Could not get canvas context')
    return () => {}
  }

  // Store reference to avoid null checks in nested functions
  const context: CanvasRenderingContext2D = ctx

  // Track if game over callback has been called
  let gameOverCallbackFired = false

  // Resize canvas to match container
  function resizeCanvas(): void {
    canvas.width = container.clientWidth
    canvas.height = container.clientHeight
  }
  resizeCanvas()

  const resizeObserver = new ResizeObserver(resizeCanvas)
  resizeObserver.observe(container)

  // Initialize game state
  let state = createGameState(canvas.width, canvas.height)

  let running = true
  let input = { left: false, right: false, launch: false }

  // Input handlers
  function handleKeyDown(e: KeyboardEvent): void {
    if (state.gameOver || state.won) {
      if (e.key === 'Enter') {
        // Restart game
        state = createGameState(canvas.width, canvas.height)
        input = { left: false, right: false, launch: false }
        gameOverCallbackFired = false
      }
      return
    }

    switch (e.key) {
      case 'ArrowLeft':
      case 'a':
        input.left = true
        e.preventDefault()
        break
      case 'ArrowRight':
      case 'd':
        input.right = true
        e.preventDefault()
        break
      case ' ':
        if (!state.started) {
          state.started = true
        } else if (!state.ballLaunched) {
          input.launch = true
        }
        e.preventDefault()
        break
    }
  }

  function handleKeyUp(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowLeft':
      case 'a':
        input.left = false
        break
      case 'ArrowRight':
      case 'd':
        input.right = false
        break
      case ' ':
        input.launch = false
        break
    }
  }

  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('keyup', handleKeyUp)

  // Game loop
  function gameLoop(): void {
    if (!running) return

    const width = canvas.width
    const height = canvas.height

    // Clear canvas
    context.fillStyle = '#0a0a0a'
    context.fillRect(0, 0, width, height)

    // Show start screen if game hasn't started
    if (!state.started) {
      const highScores = getHighScores('breakout')
      drawStartScreen(context, width, height, highScores)
      requestAnimationFrame(gameLoop)
      return
    }

    // Track previous state
    const previousGameOver = state.gameOver
    const previousWon = state.won

    // Update game
    state = updateGame(state, input)

    // Fire game over callback once when game ends
    if ((state.gameOver || state.won) && !previousGameOver && !previousWon) {
      if (!gameOverCallbackFired && onGameOver) {
        gameOverCallbackFired = true
        onGameOver(state.score)
      }
    }

    // Reset launch input after processing
    input.launch = false

    // Draw game elements
    drawPaddle(context, state.paddle)
    drawBall(context, state.ball)
    drawBricks(context, state.bricks)
    drawUI(context, state.score, state.lives, width)

    if (state.started && !state.ballLaunched) {
      drawLaunchPrompt(context, width, height)
    }

    if (state.gameOver) {
      drawGameOver(context, state.score, width, height)
    } else if (state.won) {
      drawWinScreen(context, state.score, width, height)
    }

    requestAnimationFrame(gameLoop)
  }

  // Start the game loop
  requestAnimationFrame(gameLoop)

  // Return cleanup function
  return () => {
    running = false
    document.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('keyup', handleKeyUp)
    resizeObserver.disconnect()
    container.innerHTML = ''
  }
}
